import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';

// Helper function to import all images from a folder using require.context
function importAll(r) {
  return r.keys().map(r);
}

// Dynamically import images from folders
const menApparel = importAll(require.context('../Images/Collections/Men', false, /\.(png|jpe?g|svg)$/));
const womenTops = importAll(require.context('../Images/Collections/Women/Tops', false, /\.(png|jpe?g|svg)$/));
const womenBottoms = importAll(require.context('../Images/Collections/Women/Bottoms', false, /\.(png|jpe?g|svg)$/));
const womenDresses = importAll(require.context('../Images/Collections/Women/Dresses', false, /\.(png|jpe?g|svg)$/));
const kidsApparel = importAll(require.context('../Images/Collections/Kids', false, /\.(png|jpe?g|svg)$/));
const Athleisure = importAll(require.context('../Images/Collections/Athleisure', false, /\.(png|jpe?g|svg)$/));
const Portfolio = () => {
  const [activeCategory, setActiveCategory] = useState('men');

  const categories = [
    { id: 'men', name: "Men's Apparel" },
    { id: 'kids', name: "Kids' Clothing" },
    { id: 'women-tops', name: "Women's Tops" },
    { id: 'women-bottoms', name: "Women's Bottoms" },
    { id: 'women-dresses', name: "Women's Dresses" },
    { id: 'Athleisure', name: "Athleisure" },
  ];

  const categoryImages = {
    men: menApparel,
    kids: kidsApparel,
    'women-tops': womenTops,
    'women-bottoms': womenBottoms,
    'women-dresses': womenDresses,
    Athleisure: Athleisure,
  };

  const speedMap = {
    men: 3500,
    kids: 2500,
    'women-tops': 4000,
    'women-bottoms': 4200,
    'women-dresses': 4500,
     Athleisure: 3500,
  };

  const responsive = {
    superLargeDesktop: { breakpoint: { max: 4000, min: 3000 }, items: 4 },
    desktop: { breakpoint: { max: 3000, min: 1024 }, items: 3 },
    tablet: { breakpoint: { max: 1024, min: 464 }, items: 2 },
    mobile: { breakpoint: { max: 464, min: 0 }, items: 1 },
  };

  const selectedImages = categoryImages[activeCategory];

  return (
    <section id="portfolio" className="py-12 lg:py-12 bg-gradient-to-br from-white to-honeycomb-grey-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-4 py-2 bg-honeycomb-blue-100 text-honeycomb-blue-800 rounded-full text-sm font-medium mb-6">
            Our Portfolio
          </div>

          <h2 className="text-4xl lg:text-5xl font-display font-bold text-honeycomb-grey-900 mb-6">
            Showcase of Our <span className="text-honeycomb-blue-600">Global Collections</span>
          </h2>

          <p className="text-lg text-honeycomb-grey-600 max-w-3xl mx-auto">
            Explore our diverse range of premium garments crafted for men, women, and children. Each piece represents our commitment to quality, style, and global fashion standards.
          </p>
        </motion.div>

        {/* Category Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                activeCategory === category.id
                  ? 'bg-honeycomb-blue-600 text-white shadow-lg'
                  : 'bg-white text-honeycomb-grey-700 hover:bg-honeycomb-grey-100 border border-honeycomb-grey-200'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Carousel */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Carousel
              responsive={responsive}
              infinite
              autoPlay
              autoPlaySpeed={speedMap[activeCategory]}
              showDots={false}
              arrows
              keyBoardControl
              transitionDuration={600}
              key={activeCategory}
              containerClass="carousel-container pb-8"
            >
              {selectedImages.map((image, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -8 }}
                  className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 mx-2"
                >
                  <div className="relative w-full flex justify-center items-center h-[400px] md:h-[500px] overflow-hidden">
                    <img
                      src={image}
                      alt="Collection"
                      className="w-full h-full object-contain object-center"
                    />
                  </div>
                  <div className="p-4 text-center">
                    {/* Optional captions */}
                  </div>
                </motion.div>
              ))}
            </Carousel>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Portfolio;
